export const TW_DEFAULT_CURRENCY = 'LKR';

/** Numeric amount only — en-LK grouping with 2 decimals */
export const formatTWAmountNumber = (amount) => {
  if (amount === null || amount === undefined || amount === '') return null;
  const num = Number(amount);
  if (!Number.isFinite(num)) return null;
  return num.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Display LKR amounts with Rs. prefix; other currencies use ISO code */
export const formatTWCurrency = (amount, currency = TW_DEFAULT_CURRENCY) => {
  const formatted = formatTWAmountNumber(amount);
  if (formatted === null) return '—';
  const code = String(currency || TW_DEFAULT_CURRENCY).trim().toUpperCase();
  if (code === 'LKR') return `Rs. ${formatted}`;
  return `${code} ${formatted}`;
};

/** Date only (e.g. schedule/value dates) */
export const formatTWDate = (value, fallback = '—') => {
  if (!value) return fallback;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? fallback : d.toLocaleDateString();
};

/** Date + time — use for action/event timestamps (check-in, issued, sent, etc.) */
export const formatTWDateTime = (value, fallback = '—') => {
  if (!value) return fallback;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const displayParticipantName = (p) => {
  if (!p) return '—';
  const fullName = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
  if (p.participantType === 'individual') {
    return fullName || p.companyName || '—';
  }
  if (p.participantType === 'corporate') {
    return p.companyName || fullName || '—';
  }
  return p.companyName || fullName || '—';
};

/** Mirrors backend attendee-count lock rules for linked invoices */
export const invoiceBlocksAttendeeCountChange = (invoice) => {
  if (!invoice) return false;
  if (['void', 'cancelled'].includes(invoice.status)) return false;
  if (Number(invoice.balanceAmount ?? 0) > 0.01) return true;
  if (Number(invoice.paidAmount ?? 0) > 0.01) return true;
  if (['paid', 'partially_paid'].includes(invoice.status)) return true;
  return false;
};

/** Helper copy for locked attendee count field on allocation edit */
export const attendeeCountLockHelperText = (invoice) => {
  if (!invoice?.invoiceNumber) return 'Attendee count is locked while the linked invoice has payments or an outstanding balance.';
  const reasons = [];
  if (Number(invoice.paidAmount ?? 0) > 0.01) reasons.push('payments have been recorded');
  if (Number(invoice.balanceAmount ?? 0) > 0.01) {
    reasons.push(`${formatTWCurrency(invoice.balanceAmount, invoice.currency)} is outstanding`);
  }
  const detail = reasons.length ? reasons.join(' and ') : 'the invoice is settled';
  return `Locked while invoice ${invoice.invoiceNumber} has ${detail}. Void the invoice or reverse payments before changing headcount.`;
};

export const normalizePagination = (pagination = {}, fallbackLimit = 20) => {
  const page = pagination.page ?? pagination.currentPage ?? 1;
  const limit = pagination.limit ?? fallbackLimit;
  const total = pagination.total ?? pagination.totalCount ?? 0;
  const pages = pagination.pages ?? pagination.totalPages ?? 0;
  return { page, limit, total, pages };
};

export const statusBadgeClass = (colorMap, value, fallback = 'bg-gray-100 text-gray-800 border-gray-200') => {
  const base = colorMap[value] || fallback;
  return base.includes('border') ? base : `${base} border border-current/20`;
};
